import React, {useEffect, useRef, useState} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {Stack, StackProps, useMultiStyleConfig, TextProps, Text, Box, BoxProps} from '@chakra-ui/react'
import {Icon, IconButton, NewsletterInput} from '_uiComponents'
import {useAnalytics, usePageDesigner, useViewport} from '_customHooks'
import {Page} from '_types'

/**
 * @name NewsletterModal
 *
 * @component
 *
 * @description
 * Renders the body of Newsletter modal small triggered by personalization.
 *
 * @returns {React.FC} Rendered `NewsletterModalSmall` component.
 */
const NewsletterModalSmall: React.FC = () => {
    const {isMobile} = useViewport()
    const {formatMessage} = useIntl()
    const {analyticsEventNewsletter} = useAnalytics()
    const {pageComponents} = usePageDesigner({pageId: 'overlay-newsletter'})
    const {bannerNewsletter} = mapPageDesignerData(pageComponents)
    const styles = useMultiStyleConfig('NewsletterModalSmall', {
        horizontalAlignment: bannerNewsletter.horizontalAlignment,
        verticalAlignment: bannerNewsletter.verticalAlignment,
    }) as Record<string, StackProps & TextProps & BoxProps>

    const [mailTrigger, setMailTrigger] = useState('')
    const [analyticsLabel, setAnalyticsLabel] = useState<string>()

    const refNewsletterSmall = useRef<HTMLElement>(null)
    const className = `cc-pd-overlay-newsletter ${bannerNewsletter.class}`

    const handleOnClose = () => {
        analyticsEventNewsletter({action: 'dismiss', label: 'personalization'})
        refNewsletterSmall.current!.removeAttribute('style')
    }

    const handleSubmit = () => {
        refNewsletterSmall.current!.removeAttribute('style')
    }

    useEffect(() => {
        document.addEventListener('overlaynewsletter:showmodal', (event) => {
            const target = (event as CustomEvent).detail.target
            const email = (event as CustomEvent).detail.email
            const source = (event as CustomEvent).detail.source ?? 'personalization'

            if (target) {
                analyticsEventNewsletter({action: 'mini_popup_open', label: source})

                const modalSmall = document.getElementsByClassName(target)[0] as HTMLElement

                if (modalSmall) {
                    modalSmall.style.display = 'flex'

                    if (email) {
                        setMailTrigger(email)
                    }

                    if (source) {
                        setAnalyticsLabel(source)
                    }
                }
            }
        })
    }, [])

    return (
        <Box {...styles.wrapper} data-testid="newsletter-small" css={bannerNewsletter.extraCSS} className={className} ref={refNewsletterSmall}>
            <Stack {...styles.container}>
                <Box {...styles.close}>
                    <IconButton
                        variant={'iconButtonBgTransparent'}
                        size="iconButtonSm"
                        icon={<Icon name={'IconClose'} size="md" />}
                        onClick={handleOnClose}
                        aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})}
                    />
                </Box>

                <Box {...styles.text}>
                    <Text {...styles.title}>{bannerNewsletter.title}</Text>
                    <Text {...styles.subtitle}>{bannerNewsletter.subtitle}</Text>
                </Box>

                <Stack sx={styles.input}>
                    {isMobile && (
                        <Text as="h2" {...styles.inputLabel}>
                            <FormattedMessage id="label.input.email.profile" defaultMessage="Newsletter" />
                        </Text>
                    )}

                    <NewsletterInput
                        btnLabel={bannerNewsletter.ctaLabel}
                        analyticsLabel={analyticsLabel!}
                        onSubmit={handleSubmit}
                        initialMailValue={mailTrigger}
                    />
                </Stack>
            </Stack>
        </Box>
    )
}

type NewsletterPageDesignerData = {
    class?: string
    title?: string
    subtitle?: string
    ctaLabel?: string
    verticalAlignment?: string
    horizontalAlignment?: string
    submitTarget?: string
    extraCSS?: string
}

/**
 * Maps the page designer data.
 *
 * @param {Page} [pageComponents] - The components of the page.
 * @returns {Object} An object containing the footer columns, footer copyright, and footer socials.
 * @returns {Array<FooterColumn>} columns - The footer columns.
 * @returns {FooterCopyright} [legal] - The footer copyright.
 * @returns {FooterSocials} [socials] - The footer socials.
 */
const mapPageDesignerData = (pageComponents?: Page) => {
    const bannerNewsletterData: NewsletterPageDesignerData = {}

    const components = pageComponents?.regions?.[0]?.components

    if (components) {
        const bannerNewsletter = components.filter((element) => element?.data?.overlay_style === 'small')[0]?.data
        bannerNewsletterData.class = bannerNewsletter?.class_name
        bannerNewsletterData.title = bannerNewsletter?.title_text
        bannerNewsletterData.subtitle = bannerNewsletter?.subtitle_text
        bannerNewsletterData.ctaLabel = bannerNewsletter?.cta_text
        bannerNewsletterData.verticalAlignment = bannerNewsletter?.vertical_position
        bannerNewsletterData.horizontalAlignment = bannerNewsletter?.horizontal_position
        bannerNewsletterData.submitTarget = bannerNewsletter?.submit_target
        bannerNewsletterData.extraCSS = bannerNewsletter?.extraCSS
    }

    return {
        bannerNewsletter: bannerNewsletterData,
    }
}

export {NewsletterModalSmall}
